import React, { useEffect, useRef, useState } from 'react';
import clsx from 'clsx';
import { makeStyles } from '@material-ui/core/styles';
import { ButtonBase } from '@material-ui/core';
import tokens from '../theme/tokens';

// 사업자등록증 첨부 — 기업회원 가입 신청 폼의 한 칸. 이미지·PDF 1장(8MB). 고르면 썸네일(PDF 는 표시)·파일명·크기와 [삭제]가 보이고, 칸을 다시 누르면 바꾼다
export const CERT_ACCEPT = 'image/*,application/pdf';
export const CERT_MAX_BYTES = 8 * 1024 * 1024;

const useStyles = makeStyles(() => ({
  root: {
    display: 'flex',
    alignItems: 'center',
    width: '100%',
    minHeight: 54,
    padding: '0 14px',
    borderRadius: 4,
    border: `1px dashed ${tokens.line}`,
    backgroundColor: tokens.surface,
    textAlign: 'left',
    transition: 'border-color .12s, background-color .12s',
    '&:hover': { borderColor: tokens.primary, backgroundColor: tokens.primarySoft },
  },
  filled: { padding: '7px 14px', borderStyle: 'solid', '&:hover': { backgroundColor: tokens.surface } },
  icon: { flex: 'none', width: 22, height: 22, marginRight: 10, color: tokens.muted },
  label: { flex: '1 1 auto', fontSize: 16, letterSpacing: -0.5, color: tokens.placeholder },
  hint: { flex: 'none', marginLeft: 10, fontSize: 13, color: tokens.muted },
  thumb: {
    flex: 'none', display: 'flex', alignItems: 'center', justifyContent: 'center', width: 40, height: 40, marginRight: 12,
    borderRadius: 6, overflow: 'hidden', border: `1px solid ${tokens.line}`, backgroundColor: tokens.surfaceSoft,
    '& img': { width: '100%', height: '100%', objectFit: 'cover', display: 'block' },
  },
  pdf: { fontSize: 11, fontWeight: 700, color: tokens.primaryDark },
  meta: { flex: '1 1 auto', minWidth: 0 },
  name: { display: 'block', fontSize: 15, fontWeight: 700, letterSpacing: -0.5, color: tokens.ink, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  size: { display: 'block', marginTop: 2, fontSize: 12, color: tokens.muted },
  remove: {
    flex: 'none', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 28, height: 28, marginLeft: 8, marginRight: -6,
    borderRadius: 14, color: tokens.muted, cursor: 'pointer',
    '&:hover': { color: tokens.primaryDark, backgroundColor: tokens.primarySoft },
    '& svg': { width: 14, height: 14 },
  },
  error: { marginTop: 6, fontSize: 13, color: tokens.primaryDark },
}));

const isImage = (f) => Boolean(f) && /^image\//.test(f.type);
const fmtSize = (n) => (n >= 1024 * 1024 ? `${(n / 1024 / 1024).toFixed(1)}MB` : `${Math.max(1, Math.round(n / 1024))}KB`);

function BizCertField({ value, onChange, className }) {
  const cs = useStyles();
  const fileRef = useRef(null);
  const [error, setError] = useState('');
  const [preview, setPreview] = useState('');

  useEffect(() => {
    if (!isImage(value)) { setPreview(''); return undefined; }
    const url = URL.createObjectURL(value);
    setPreview(url);
    return () => URL.revokeObjectURL(url);
  }, [value]);

  const pick = (e) => {
    const f = e.target.files && e.target.files[0];
    e.target.value = '';
    if (!f) return;
    if (!isImage(f) && f.type !== 'application/pdf') { setError('이미지 또는 PDF 파일만 올릴 수 있습니다.'); return; }
    if (f.size > CERT_MAX_BYTES) { setError('8MB 이하 파일만 올릴 수 있습니다.'); return; }
    setError('');
    onChange(f);
  };
  const remove = (e) => { e.stopPropagation(); setError(''); onChange(null); };

  return (
    <div className={className}>
      <ButtonBase className={clsx(cs.root, value && cs.filled)} onClick={() => fileRef.current && fileRef.current.click()} focusRipple={false} aria-label="사업자등록증 첨부">
        {value ? (
          <React.Fragment>
            <span className={cs.thumb}>{preview ? <img src={preview} alt="" /> : <span className={cs.pdf}>PDF</span>}</span>
            <span className={cs.meta}>
              <span className={cs.name}>{value.name}</span>
              <span className={cs.size}>사업자등록증 · {fmtSize(value.size)}</span>
            </span>
            <span role="button" tabIndex={-1} className={cs.remove} onClick={remove} aria-label="첨부 삭제">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true"><path d="M6 6l12 12M18 6 6 18" /></svg>
            </span>
          </React.Fragment>
        ) : (
          <React.Fragment>
            <svg className={cs.icon} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8z" /><path d="M14 3v5h5" /><path d="M12 11v6M9 14h6" />
            </svg>
            <span className={cs.label}>사업자등록증 첨부</span>
            <span className={cs.hint}>이미지 · PDF</span>
          </React.Fragment>
        )}
      </ButtonBase>
      <input ref={fileRef} type="file" accept={CERT_ACCEPT} hidden onChange={pick} />
      {error && <div className={cs.error}>{error}</div>}
    </div>
  );
}

export default BizCertField;
